import { useEffect, useRef, useState, type ComponentProps } from 'react';
import type { Objective } from '../api/missions';
import { ca } from '../i18n/ca';
import { OBJECTIVE_ICONS } from './objectiveIcons';
import { MissionMap } from './MissionMap';
import { gridRef } from './sessionState';

type MapProps = Omit<
  ComponentProps<typeof MissionMap>,
  'height' | 'selected' | 'onSelect' | 'grid'
>;

/** Metres per square of the optional grid: the kilometre squares a player reads in game. */
const GRID = 1000;

/**
 * The map tab (UI doc 5.1): the session's map as a full-screen layer over the
 * page, the objectives with their task icons, a kilometre grid on demand, and
 * a bottom sheet that snaps to the objective tapped, with its type, state and
 * grid. Escape or the close button hands the page back where it was.
 *
 * What the design adds and the platform cannot draw yet stays off it: the
 * friendly squads' positions (the panel's live endpoint, roadmap R3.3) and a
 * long-pressed marker for my side's phones, which needs somewhere to keep it.
 */
export function SessionMapLayer({
  map,
  objectives,
  states,
  titleOf,
  selected,
  onSelect,
  onClose,
}: {
  map: MapProps;
  objectives: Objective[];
  states: Map<string, { state: string; title: string }>;
  titleOf: (o: Objective) => string;
  selected: string | null;
  onSelect: (id: string | null) => void;
  onClose: () => void;
}) {
  const [grid, setGrid] = useState(false);
  // The map is a square as large as the space between the head and the sheet.
  // A first guess from the window stands in until the body has measured itself.
  const [height, setHeight] = useState(() =>
    typeof window === 'undefined' ? 600 : Math.max(240, window.innerHeight - 200),
  );
  const closeButton = useRef<HTMLButtonElement>(null);
  const body = useRef<HTMLDivElement>(null);

  // The body's own height is the room the map has: the window minus a guess
  // clipped the map on a phone, where the bar, the sheet and the browser's own
  // toolbars take more than any constant. A box not laid out yet reads 0
  // (memory `unlaid-out-container-measures-zero`), and 0 keeps the last size.
  useEffect(() => {
    const el = body.current;
    if (!el || typeof ResizeObserver === 'undefined') {
      const fromWindow = () => setHeight(Math.max(240, window.innerHeight - 200));
      window.addEventListener('resize', fromWindow);
      return () => window.removeEventListener('resize', fromWindow);
    }
    const observer = new ResizeObserver((entries) => {
      const measured = Math.floor(entries[0]?.contentRect.height ?? 0);
      if (measured > 0) setHeight(measured);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Escape closes the layer, the same contract as the other dialogs; the
  // focus starts on the close button so a keyboard is never trapped behind it.
  // Once, on opening: the page re-renders every second for its clock, and a
  // handler passed fresh each time must not steal the focus back each time.
  const close = useRef(onClose);
  close.current = onClose;
  useEffect(() => {
    closeButton.current?.focus();
    function onKey(e: KeyboardEvent) {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      close.current();
    }
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, []);

  const shown = objectives.find((o) => o.id === selected);
  const state = shown ? (states.get(shown.id)?.state ?? 'pending') : null;
  const grid6 = shown ? gridRef(shown.position) : null;
  return (
    <div className="map-layer" role="dialog" aria-modal="true" aria-label={ca.sessionNow.mapTitle}>
      <div className="map-layer-head">
        <h2>{ca.sessionNow.mapTitle}</h2>
        <button type="button" className="small" aria-pressed={grid} onClick={() => setGrid(!grid)}>
          <i className="bi bi-grid-3x3" aria-hidden="true" /> {ca.mapUi.grid}
        </button>
        <button type="button" className="small" ref={closeButton} onClick={onClose}>
          <i className="bi bi-x-lg" aria-hidden="true" /> {ca.sessionNow.mapClose}
        </button>
      </div>
      <div className="map-layer-body" ref={body}>
        <MissionMap
          {...map}
          height={height}
          grid={grid ? GRID : 0}
          selected={selected}
          onSelect={(sel) =>
            onSelect(sel.kind === 'objective' ? (selected === sel.id ? null : sel.id) : null)
          }
        />
      </div>
      <section className="map-sheet" aria-label={ca.sessionNow.details}>
        {shown ? (
          <>
            <div className="row">
              <i
                className={`bi bi-${OBJECTIVE_ICONS[shown.type] ?? 'geo-alt'}`}
                aria-hidden="true"
              />
              <strong className="grow">{titleOf(shown)}</strong>
              <button
                type="button"
                className="small ghost"
                aria-label={ca.sessionNow.sheetClose}
                onClick={() => onSelect(null)}
              >
                <i className="bi bi-x" aria-hidden="true" />
              </button>
            </div>
            <dl className="facts">
              <div>
                <dt>{ca.sessionNow.type}</dt>
                <dd>{ca.objectiveUi.types[shown.type] ?? shown.type}</dd>
              </div>
              <div>
                <dt>{ca.sessionNow.state}</dt>
                <dd>{ca.objectiveUi.states[state ?? ''] ?? state}</dd>
              </div>
              {grid6 && (
                <div>
                  <dt>{ca.sessionNow.grid}</dt>
                  <dd className="num">{grid6}</dd>
                </div>
              )}
            </dl>
          </>
        ) : (
          <p className="muted small">{ca.sessionNow.sheetEmpty}</p>
        )}
      </section>
    </div>
  );
}
